import { useNavigate, useRouterState } from '@tanstack/react-router';
import * as React from 'react';
import { SPEC_ROUTE_SEGMENT } from '../../lib/spec-config.js';
import { SPEC_CHAT_SEARCH_KEY } from '../../lib/spec-conversations.js';
import {
  type SpecFollowPreference,
  type SpecFollowStorage,
  clampSpecPanelWidth,
  defaultSpecFollowStorage,
  readSpecFollowPreference,
  specFollowStorageKey,
  writeSpecFollowPreference,
} from '../../lib/spec-follow-storage.js';
import { getWorkspaceScopedPath } from '../app-sidebar-nav.js';
import { useOptionalSpecRuntime } from './runtime-provider.js';

/**
 * Presentation state for follow mode: closed, the left panel, or full page.
 *
 * A third surface for the same conversation, not a second one. The Spec
 * runtime (`runtime-provider.tsx`) already holds the thread, the stream, and
 * the pending send; this module owns only WHERE that thread is currently
 * displayed, and how wide the panel is while it is. Nothing here calls a
 * runtime method that changes what the runtime holds: `close()` does not
 * cancel a run, and `openFullPage()` navigates rather than reopening the
 * conversation the runtime already has. The thing that actually protects a
 * handoff from refetching history mid-park is `page.tsx`'s
 * `conversationId === chatId` guard; this module's job is simply to never
 * call `openConversation` itself.
 */

export interface SpecFollowState {
  mode: 'closed' | 'panel' | 'full_page';
  panelWidth: number;
  activeConversationId: string | null;
}

export interface SpecFollowController extends SpecFollowState {
  openPanel(): void;
  close(): void;
  openFullPage(): void;
  setPanelWidth(width: number): void;
}

const SpecFollowContext = React.createContext<SpecFollowController | null>(null);

export function useSpecFollow(): SpecFollowController {
  const controller = React.useContext(SpecFollowContext);
  if (!controller) {
    throw new Error('useSpecFollow must be used inside a SpecFollowProvider.');
  }
  return controller;
}

/**
 * Returns null when follow mode is not mounted, the same way
 * `useOptionalSpecRuntime` does.
 *
 * Load-bearing for the app shell rather than a convenience. `follow-layout.tsx`
 * wraps main content on EVERY route, and in production none of the Spec
 * providers are mounted at all (`isSpecWorkerConfigured()` is false, so
 * `SpecWorkspaceRuntime` renders its children bare). A layout that insisted on
 * this context would throw on every page in Parcel, not just on Spec.
 */
export function useOptionalSpecFollow(): SpecFollowController | null {
  return React.useContext(SpecFollowContext);
}

/**
 * Spec's own route, as a path.
 *
 * Derived from the same `SPEC_ROUTE_SEGMENT` that `spec-context-adapters.ts`
 * builds its ROUTE IDS from. The two functions stay separate on purpose -- a
 * pathname and a route id disagree about a workspace whose slug is literally
 * `spec` -- but a rename must not be able to reach only one of them.
 */
const SPEC_ROUTE_PATH = `/${SPEC_ROUTE_SEGMENT}` as const;

/**
 * Whether `pathname` already names the full-page Spec route, bare or
 * workspace-scoped.
 *
 * Scopes `pathname`'s own Spec link with the sidebar's helper and compares
 * it back to itself, rather than a second regex: `getWorkspaceScopedPath`
 * already knows both forms (`/spec` outside a workspace, `/<slug>/spec`
 * inside one), so this is exactly "does this path already point at the route
 * it would point at".
 */
export function isSpecFullPageRoute(pathname: string): boolean {
  return pathname === getWorkspaceScopedPath(pathname, SPEC_ROUTE_PATH);
}

/** `full_page` wins over `open` while the route names it; otherwise `open` decides. */
export function deriveSpecFollowMode(
  isFullPageRoute: boolean,
  open: boolean,
): SpecFollowState['mode'] {
  if (isFullPageRoute) return 'full_page';
  return open ? 'panel' : 'closed';
}

export interface SpecFollowStateProviderProps {
  /** The route the member is on right now; `mode` reads `full_page` off this. */
  pathname: string;
  workspaceId: string | undefined;
  memberId: string | undefined;
  /**
   * Read through from the runtime, never a second copy. A duplicate here
   * would be exactly the split-brain PR 07's route-as-single-writer rule
   * exists to prevent: the runtime is the one thing both surfaces share.
   */
  activeConversationId: string | null;
  /** Navigate to the full-page route. The caller owns the router. */
  onOpenFullPage: (activeConversationId: string | null) => void;
  /** Defaults to `window.localStorage`, guarded; inject a stub in tests. */
  storage?: SpecFollowStorage | null;
  children: React.ReactNode;
}

/**
 * Presentation state, driven entirely by props.
 *
 * No `useRouterState`, no `useOptionalSpecRuntime`, no `useNavigate`: mirrors
 * the split in `spec-context-provider.tsx` between a pure store-driven
 * provider and the router/runtime wiring around it, so the persistence and
 * derivation rules below -- clamping, per-workspace/member scoping, deriving
 * `full_page` from a route -- are testable with a plain pathname string and a
 * storage stub, with no router or runtime mounted at all.
 */
export function SpecFollowStateProvider({
  pathname,
  workspaceId,
  memberId,
  activeConversationId,
  onOpenFullPage,
  storage = defaultSpecFollowStorage(),
  children,
}: SpecFollowStateProviderProps) {
  const key = specFollowStorageKey(workspaceId, memberId);
  const [preference, setPreference] = React.useState<SpecFollowPreference>(() =>
    readSpecFollowPreference(key, storage),
  );

  // A different key is a different member's preference, not a value to carry
  // forward: re-read on a workspace switch or a member resolving, the same
  // moment the runtime itself resets the thread it holds.
  const lastKey = React.useRef(key);
  React.useEffect(() => {
    if (lastKey.current === key) return;
    lastKey.current = key;
    setPreference(readSpecFollowPreference(key, storage));
  }, [key, storage]);

  const openPanel = React.useCallback(() => {
    setPreference((previous) => {
      if (previous.open) return previous;
      const next = { ...previous, open: true };
      writeSpecFollowPreference(key, next, storage);
      return next;
    });
  }, [key, storage]);

  const close = React.useCallback(() => {
    setPreference((previous) => {
      if (!previous.open) return previous;
      const next = { ...previous, open: false };
      writeSpecFollowPreference(key, next, storage);
      return next;
    });
  }, [key, storage]);

  // Navigation only -- see the module comment. Expanding to full page must
  // never reopen the conversation the runtime already holds, so this never
  // touches the runtime, only asks the caller to move the router.
  const openFullPage = React.useCallback(() => {
    onOpenFullPage(activeConversationId);
  }, [onOpenFullPage, activeConversationId]);

  const setPanelWidth = React.useCallback(
    (width: number) => {
      setPreference((previous) => {
        const clamped = clampSpecPanelWidth(width);
        if (previous.width === clamped) return previous;
        const next = { ...previous, width: clamped };
        writeSpecFollowPreference(key, next, storage);
        return next;
      });
    },
    [key, storage],
  );

  const isFullPageRoute = isSpecFullPageRoute(pathname);
  const controller = React.useMemo<SpecFollowController>(
    () => ({
      mode: deriveSpecFollowMode(isFullPageRoute, preference.open),
      panelWidth: preference.width,
      activeConversationId,
      openPanel,
      close,
      openFullPage,
      setPanelWidth,
    }),
    [
      isFullPageRoute,
      preference,
      activeConversationId,
      openPanel,
      close,
      openFullPage,
      setPanelWidth,
    ],
  );

  return <SpecFollowContext.Provider value={controller}>{children}</SpecFollowContext.Provider>;
}

export interface SpecFollowProviderProps {
  children: React.ReactNode;
}

/**
 * Wires the router and the Spec runtime into follow-mode presentation state.
 *
 * Reads the runtime with `useOptionalSpecRuntime`, not `useSpecRuntime`, and
 * renders children unharmed when it is null (production with no Spec Worker
 * configured, where the runtime is never mounted at all). The launcher and
 * panel this state feeds are gated on that same runtime and simply do not
 * render without it; this provider only needs to not be the thing that
 * throws first.
 */
export function SpecFollowProvider({ children }: SpecFollowProviderProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navigate = useNavigate();
  const runtime = useOptionalSpecRuntime();

  /**
   * Binding ruling: navigate to `/spec?c=<activeConversationId>` when the
   * runtime holds a conversation, never to bare `/spec`. `page.tsx` starts a
   * new conversation whenever the route names none, and that aborts the live
   * SSE reader -- so landing on bare `/spec` would destroy the very thread
   * this handoff exists to preserve. Routing through `?c=` instead lands on
   * `page.tsx`'s `conversationId === chatId` guard, the no-refetch path the
   * park-frame counting rule depends on.
   */
  const onOpenFullPage = React.useCallback(
    (activeConversationId: string | null) => {
      const to = getWorkspaceScopedPath(pathname, SPEC_ROUTE_PATH);
      void navigate(
        activeConversationId
          ? { to, search: { [SPEC_CHAT_SEARCH_KEY]: activeConversationId } }
          : { to },
      );
    },
    [navigate, pathname],
  );

  return (
    <SpecFollowStateProvider
      pathname={pathname}
      workspaceId={runtime?.workspaceId}
      memberId={runtime?.bootstrap?.memberId}
      activeConversationId={runtime?.conversationId ?? null}
      onOpenFullPage={onOpenFullPage}
    >
      {children}
    </SpecFollowStateProvider>
  );
}
